import { useRef } from 'react';
import { HistoricalCorrespondenceSection } from '@/features/historical-correspondence/HistoricalCorrespondenceSection';
import { HistoricalWorkflowSection } from '@/features/historical-workflow/HistoricalWorkflowSection';
import { HistoricalNotesSection } from '@/features/historical-notes/HistoricalNotesSection';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ApiError } from '@/api/errors';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import { useCommand } from '@/app/commands';
import { useOrg, useOrgId } from '@/app/session';
import { Avatar, Button, EmptyState, ErrorState, Icon, LinkButton, LoadingState, Section, StatusDot, Timeline, type TimelineEntry } from '@/components/ui';
import { humanize } from '@/lib/format';
import { cx } from '@/lib/cx';
import { subjectWithoutLocation } from '@/features/conversations/api';
import { RowTime } from '@/features/conversations/parts';
import { conversationSignal, SignalDot } from '@/features/conversations/signal';
import { snippetOf } from '@/features/conversations/snippet';
import { conversationsQuery, locationKeys, type Location } from './api';
import { counterpartName } from './live';

// ---------- Conversations ----------

/** Threads at the site, as rows like the conversation list: who, their company, the gist, when, and what is owed. */
export function ConversationsSection({ location: l }: { location: Location }) {
  const orgId = useOrgId();
  const q = useQuery(conversationsQuery(orgId, l.id));
  const items = (q.data?.items ?? []).filter((c) => c.state !== 'archived');
  return (
    <Section
      id="conversations"
      title="Conversations"
      actions={
        l.deleted_at ? null : (
          <LinkButton size="sm" variant="quiet" icon="plus" to="/app/conversations/new" search={{ location_id: l.id }}>
            Start conversation
          </LinkButton>
        )
      }
    >
      {q.isPending ? (
        <LoadingState compact lines={2} label="Loading conversations" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState compact title="No conversations" />
      ) : (
        <ul className="site-rows">
          {items.map((c) => {
            const name = counterpartName(c);
            const company = c.counterpart?.company_name;
            const signal = conversationSignal(c);
            const gist = snippetOf(c.latest_message?.snippet) || subjectWithoutLocation(c.title, l.name);
            const unread = c.unread_count > 0;
            return (
              <li key={c.id}>
                <Link to="/app/conversations/$conversationId" params={{ conversationId: c.id }} className={cx('site-row conv-row', unread && 'is-unread')}>
                  <Avatar name={name} company={company} size={32} />
                  <span className="site-row-main">
                    <span className="site-row-line">
                      <span className="site-row-name name truncate">
                        {unread ? <span className="visually-hidden">Unread. </span> : null}
                        {name}
                      </span>
                      {company ? <span className="site-row-sub truncate">{company}</span> : null}
                      <RowTime value={c.last_message_at} className="site-row-time" />
                    </span>
                    <span className="conv-row-gist truncate">{gist}</span>
                    {signal ? (
                      <SignalDot signal={signal} className="conv-row-mark" />
                    ) : c.state === 'closed' ? (
                      <StatusDot tone="faint" className="conv-row-mark">
                        Closed
                      </StatusDot>
                    ) : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// ---------- Files ----------

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function FilesSection({ location: l }: { location: Location }) {
  const orgId = useOrgId();
  const input = useRef<HTMLInputElement>(null);
  const upload = useCommand({
    run: async (file: File) => {
      const isImage = file.type.startsWith('image/');
      const up = await unwrap(
        api.POST('/api/v1/orgs/{orgId}/uploads', {
          params: { path: { orgId }, header: { 'Idempotency-Key': newIdempotencyKey() } },
          body: {
            purpose: isImage ? 'location_image' : 'source_document',
            filename: file.name,
            expected_size: file.size,
            expected_sha256: await sha256Hex(file),
            declared_mime: file.type || undefined,
          },
        }),
      );
      const sent = await unwrap(
        api.PUT('/api/v1/orgs/{orgId}/uploads/{uploadId}/content', {
          params: { path: { orgId, uploadId: up.id }, header: { 'If-Match': ifMatch(up.version), 'Idempotency-Key': newIdempotencyKey() } },
          body: file as unknown as string,
          bodySerializer: (b: unknown) => b as BodyInit,
          headers: { 'Content-Type': 'application/octet-stream' },
        }),
      );
      const done = await unwrap(
        api.POST('/api/v1/orgs/{orgId}/uploads/{uploadId}/complete', {
          params: { path: { orgId, uploadId: up.id }, header: { 'If-Match': ifMatch(sent.version), 'Idempotency-Key': newIdempotencyKey() } },
        }),
      );
      // The virus scan runs after completion; only clean files can be attached.
      let status = done.blob.status;
      for (let i = 0; i < 30 && (status === 'uploading' || status === 'quarantined'); i++) {
        await new Promise((r) => window.setTimeout(r, 1000));
        status = (await unwrap(api.GET('/api/v1/orgs/{orgId}/media/{blobId}', { params: { path: { orgId, blobId: done.blob.id } } }))).status;
      }
      if (status !== 'clean') {
        const detail =
          status === 'rejected'
            ? 'Failed virus check'
            : 'Virus check pending · Retry later';
        throw new ApiError(422, { code: 'media_not_clean', title: 'File not added', detail });
      }
      return unwrap(
        api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/media', {
          params: { path: { orgId, locationId: l.id }, header: { 'If-Match': ifMatch(l.version), 'Idempotency-Key': newIdempotencyKey() } },
          body: { blob_id: done.blob.id, kind: isImage ? 'image' : 'document', caption: file.name },
        }),
      );
    },
    success: (m) => `${m.caption ?? 'File'} added`,
    invalidate: [locationKeys.detail(orgId, l.id)],
  });
  const files = l.media.items;
  return (
    <Section
      id="files"
      title="Files"
      actions={
        l.deleted_at ? null : (
          <>
            <input
              ref={input}
              type="file"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) upload.mutate(f);
              }}
            />
            <Button size="sm" variant="quiet" icon="plus" loading={upload.isPending} onClick={() => input.current?.click()}>
              {upload.isPending ? 'Uploading' : 'Upload file'}
            </Button>
          </>
        )
      }
    >
      {files.length === 0 ? (
        <EmptyState compact title="No files" />
      ) : (
        <ul className="site-rows">
          {files.map((m) => (
            <li key={m.id}>
              <a className="site-row file-row" href={`/api/v1/orgs/${orgId}/media/${m.blob_id}/content`} target="_blank" rel="noreferrer">
                <span className="file-row-icon" aria-hidden>
                  <Icon name="file" size={16} />
                </span>
                <span className="site-row-name truncate">{m.caption ?? 'Untitled file'}</span>
                <span className="site-row-sub">{humanize(m.kind)}</span>
              </a>
            </li>
          ))}
          {l.media.truncated ? <li className="site-row-note faint">More files {l.media.total_count - files.length}</li> : null}
        </ul>
      )}
    </Section>
  );
}

// ---------- History ----------

const PATH_LABELS: Record<string, string> = {
  name: 'name',
  description: 'description',
  address: 'address',
  pipeline_state: 'stage',
  available_capacity_kw: 'available power',
  existing_capacity_kw: 'existing power',
  planned_capacity_kw: 'planned power',
  voltage_kv: 'voltage',
  grid_connection_status: 'grid connection',
  earliest_expansion_date: 'earliest expansion',
  redundancy_notes: 'redundancy notes',
  networks: 'networks',
  municipal_approvals: 'permits',
  contacts: 'people',
  companies: 'companies',
  media: 'files',
  automation_mode: 'automation',
  handoff_state: 'handoff',
};

function pathLabel(path: string): string {
  const parts = path.split(/[/.]/).filter(Boolean);
  if (parts[0] === 'facts' && parts[1]) return humanize(parts[1]).toLowerCase();
  if (parts[0] === 'address') return 'address';
  for (const p of [...parts].reverse()) if (PATH_LABELS[p]) return PATH_LABELS[p];
  return humanize(parts[parts.length - 1] ?? path).toLowerCase();
}

export function HistorySection({ location: l, open, onToggle }: { location: Location; open: boolean; onToggle: () => void }) {
  const { orgId, hasScope } = useOrg();
  const allowed = hasScope('audit:content');
  const q = useInfiniteQuery({
    enabled: allowed && open,
    queryKey: locationKeys.history(orgId, l.id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/locations/{locationId}/history', {
          params: { path: { orgId, locationId: l.id }, query: { limit: 25, cursor: pageParam } },
          signal,
        }),
      ),
    getNextPageParam: (last) => (last.page.has_more ? (last.page.next_cursor ?? undefined) : undefined),
  });
  const entries: TimelineEntry[] = (q.data?.pages.flatMap((p) => p.items) ?? []).map((r) => {
    const fields = [...new Set(r.changes.map((c) => pathLabel(c.field_path)))];
    return {
      id: r.id,
      at: r.created_at,
      tone: r.actor.kind === 'human' ? 'neutral' : 'faint',
      title: r.reason,
      meta: <span className="xsmall faint">{r.actor.kind === 'human' ? r.actor.display_name : `${r.actor.display_name} (automatic)`}</span>,
      body: fields.length ? <span className="small muted">Changed {fields.join(', ')}</span> : undefined,
    };
  });
  // Change history needs audit access; without it the section is not offered at all.
  if (!allowed) return null;
  return (
    <>
      <HistoricalNotesSection locationId={l.id} />
      <HistoricalCorrespondenceSection locationId={l.id} /><HistoricalWorkflowSection locationId={l.id} />
      <Section
        id="history"
        title="History"
        actions={
          <Button size="sm" variant="quiet" icon={open ? 'chevronDown' : 'chevronRight'} aria-expanded={open} aria-controls="loc-history-body" onClick={onToggle}>
            {open ? 'Hide' : 'Show'}
          </Button>
        }
      >
        {open ? (
          <div id="loc-history-body">
            {q.isPending ? (
              <LoadingState compact label="Loading history" />
            ) : q.isError ? (
              <ErrorState error={q.error} onRetry={() => void q.refetch()} />
            ) : entries.length === 0 ? (
              <EmptyState compact title="No changes" />
            ) : (
              <div className="stack">
                <Timeline entries={entries} label="Changes, newest first" />
                {q.hasNextPage ? (
                  <div>
                    <Button size="sm" variant="quiet" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                      Show older
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </Section>
    </>
  );
}
